import { ref, computed, useTemplateRef, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import ApiService from '../services/ApiService.js';

// Reusable sorting function for pages by slug
export const sortBySlug = (a, b) => {
  const slugA = a.slug;
  const slugB = b.slug;
  const numA = parseInt(slugA, 10);
  const numB = parseInt(slugB, 10);
  if (!isNaN(numA) && !isNaN(numB)) {
    return numA - numB;
  }
  return String(slugA).localeCompare(String(slugB));
};

// State
const pagesList = ref([]);
const menu = ref([]);  // Just contains slugs and basic info, not full content
const settings = ref({});
const iconMenu = ref([]); // Pages that should appear in the icon menu (addToMenu: true)
const loading = ref(true);
const error = ref(null);
const carousel = useTemplateRef('carousel');

/**
 * Scrolls the carousel and window to the top
 */
function scrollToTop() {
    // Scroll the carousel viewport to top
    const carouselViewport = document.querySelector('.p-carousel-viewport');
    if (carouselViewport) {
        carouselViewport.scrollTop = 0;
    }
    
    // Also scroll the window to top as a fallback
    window.scrollTo(0, 0);
}

export function useNavigation() {
    const router = useRouter();
    const route = useRoute();

    const currentPageIndex = computed(() => {
        if (route?.params?.slug && pagesList.value.length > 0) {
            const index = pagesList.value.findIndex(page => page.slug === route.params.slug);
            return index >= 0 ? index : 0;
        }
        return 0;
    });

    // Handle scroll to top when route changes
    watch(currentPageIndex, (newIndex, oldIndex) => {
        if (newIndex !== oldIndex) {
            scrollToTop();
        }
    });

  // Main fetch method to get page slugs and settings from API
  const fetchData = async () => {
    loading.value = true;
    error.value = null;

    try {
      // Fetch settings using cached API service
      settings.value = await ApiService.getSettings();

      // Fetch pages using cached API service
      const pagesData = await ApiService.getPages();

      // Extract the pages list
      let pages = [];
      let menuPages = [];

      if (pagesData && Array.isArray(pagesData.pages)) {
        pages = pagesData.pages;
        menuPages = pagesData.pages.filter(page => page.addToMenu === true);
      } else if (pagesData && Array.isArray(pagesData)) {
        pages = pagesData;
        menuPages = pagesData.filter(page => page.addToMenu === true);
      }

      // Sort both arrays using the exported sortBySlug function
      pages.sort(sortBySlug);
      menuPages.sort(sortBySlug);

      pagesList.value = pages;
      iconMenu.value = menuPages;

      return {
        settings: settings.value,
        pages: pagesList.value,
        iconMenu: iconMenu.value,
        currentPageIndex: currentPageIndex.value
      };
    } catch (err) {
      console.error('Error fetching navigation data:', err);
      error.value = 'Failed to load navigation. Please try again later.';
      return null;
    } finally {
      loading.value = false;
    }
  };

  const navigateWithTransition = async (slug, index) => {
    currentPageIndex.value = index
    router.push(`/pages${slug}`);
  };

  const goToNextPage = () => {
    if (currentPageIndex.value === -1) {
      goToFirstPage();
    } else if (hasNextPage()) {
      navigateWithTransition(`/${nextPageSlug()}`, currentPageIndex.value + 1);
    }
  };

  const goToPreviousPageOrHome = () => {
    if (hasPreviousPage()) {
      navigateWithTransition(`/${previousPageSlug()}`, currentPageIndex.value - 1);
    } else {
      goHome();
    }
  };

  const goHome = () => {
    navigateWithTransition('/1', 0);
  };

  const goToFirstPage = () => {
    if (pagesList?.value?.length > 0) {
      navigateWithTransition(`/${firstPageSlug()}`, 0);
    }
  };

  // Navigate to a specific page with transition animation
  const goToPage = (pageSlug) => {
    if (!pageSlug) return;
    navigateWithTransition(`/${pageSlug}`, pagesList.value.findIndex(page => page.slug === pageSlug));
  };

  const hasNextPage = () => currentPageIndex.value + 1 < pagesList.value.length;
  const nextPageSlug = () => pagesList.value[currentPageIndex.value + 1]?.slug
  const hasPreviousPage = () => currentPageIndex.value - 1 >= 0
  const previousPageSlug = () => pagesList.value[currentPageIndex.value - 1]?.slug
  const hasFirstPage = () => pagesList.value?.length > 0
  const firstPageSlug = () => pagesList.value[0]?.slug

  // Return everything needed by components
  return {
    // State
    pagesList,
    currentPageIndex,
    settings,
    loading,
    error,
    iconMenu,

    // Methods
    hasNextPage,
    hasPreviousPage,
    nextPageSlug,
    previousPageSlug,
    hasFirstPage,
    firstPageSlug,
    fetchData,
    scrollToTop,
    goToNextPage,
    goToPreviousPageOrHome,
    navigateWithTransition,
    goToFirstPage,
    goHome,
    goToPage,
};
}